import { IConfiguration } from '../../../domain/conf'
import { Store } from '../../../utils/storage/keys'
import { LocalStore } from '../../../utils/storage/local'
import { IProfile } from '../../common/profile'
import { AdSize, IAd } from '../ad'

interface IWindow extends Window {
    isBrowsi: any
    MutationObserver: any
    WebKitMutationObserver: any
}

declare let window: IWindow

export class Browsi {

    /**
     * Checks if Browsi is turned on for the page and the page is not sponsored,
     * and if so integrates Browsi into the page
     * @param conf The configuration holding page-data
     */
    public static setupBrowsi(conf: IConfiguration): void {
        if (conf.ads.browsiIntegration && !Browsi.checkPageSponsored(conf) && !Browsi.checkAdLight()) {
            const bp = conf.ads.browsiPercentage
            Browsi.integrateBrowsi(bp >= 0 && bp <= 1 ? bp : 1) // ensure a reasonable value is passed
        }
    }

    /**
     * Removes all banner ad divs except for the topmost.
     */
    public static removeAdDivs(): void {
        // Do not remove the topmost or native ads
        const adDivs = document.querySelectorAll('.ad__section-border:not(.ad__section-border--topmost)')
        for (const adDiv of adDivs) {
            adDiv.remove()
        }
    }

    public static addBrowsiToPage(conf: IConfiguration, ads: IAd[]): void {
        if (conf.page.type === 'story') {
            Browsi.removeAdDivs()
            Browsi.addBrowsiToAds(conf, Browsi.getTopAd(ads))
        } else {
            Browsi.addBrowsiToAds(conf, Browsi.filterAds(ads, true))
        }
    }

    /**
     * @param conf The configuration holding page-data
     * @param ads The ads that Browsi will be added to
     * Adds Browsi API to ads, with sizes and ad unit, and sets an event listener for ad load
     * and valid sizes
     */
    public static addBrowsiToAds(conf: IConfiguration, ads: IAd[]): void {
        for (const ad of ads) {
            const adDiv = document.getElementById(ad.id) as HTMLElement
            const position = ad.id === 'ad-1' ? 'top' : 'bottom'
            const browsiAPI =
            `<script>
            window.browsitag.cmd.push(() => {
             window.browsitag.display("${ad.id}", {
                sizes: ${this.formatAdSizes(ad.validSizes())},
                adUnit: ${this.getAdUnitPath(conf)},
                disableViewabilityOptimization: false,
                positionTag: "${position}"
              });
            });
            </script>`

            const browsiEl = document.createRange().createContextualFragment(browsiAPI)
            adDiv.append(browsiEl)
            this.observeForAdLoad(adDiv)
        }
    }

    /**
     * Observes whenever the DOM of the adDiv is changed and calls the observeDOM function
     * @param adDiv The div to be observed
     */
    public static observeForAdLoad(adDiv: HTMLElement): void {
        const observeDOM = (() => {
            const MutationObserver = window.MutationObserver || window.WebKitMutationObserver

            return (obj: any, callback: any) => {
              if( !obj || obj.nodeType !== 1 ) { return }

              if( MutationObserver ) {
                // define a new observer
                const mutationObserver = new MutationObserver(callback)

                // have the observer observe foo for changes in children
                mutationObserver.observe( obj, { childList:true, subtree:true })
                return mutationObserver
              } else if( window.addEventListener ) {
                obj.addEventListener('DOMNodeInserted', callback, false)
                obj.addEventListener('DOMNodeRemoved', callback, false)
              }
            }
          })()

        observeDOM( adDiv, () => {
            Browsi.onAdLoaded(adDiv)
         })
    }

    /**
     * Removes the ad placeholder div, and removes any inline styling on interscroller ads
     * @param adDiv
     */
    public static onAdLoaded(adDiv: HTMLElement): void {
        const ph = adDiv.getElementsByClassName('ad__placeholder')[0]
        if (ph) {
            ph.remove()
        }
        if (adDiv.innerHTML.match(/interscroller/)) {
            adDiv.setAttribute('style', '')
        }
    }

    /**
     * Get the ad unit path from page-data
     */
    public static getAdUnitPath(conf: IConfiguration): string {
        let adUnit = ''
        adUnit += '/' + conf.ads.provider.account
        adUnit += '/' + conf.ads.site
        if (Array.isArray(conf.page.ancestry) && conf.page.ancestry.length) {
            for (const ancestor of conf.page.ancestry) {
                adUnit += '/' + ancestor
            }
        }
        if (conf.page.type === 'story') {
            adUnit += '/' + 'story'
        } else {   // other cases should be 'index' or 'category', both of which map to 'index'
            adUnit += '/' + 'index'
        }
        return '"' + adUnit + '"'
    }

    /**
     * Randomly assigns the user to an A/B test bucket based on browsi_percentage.
     * Returns the a boolean denoting the bucket.
     */
    public static setABCookie(browsiPercentage: number): boolean {
        const name = 'browsi_AB_test'
        const value = Math.random() <= browsiPercentage
        const date = new Date()
        // tslint:disable-next-line: no-magic-numbers
        date.setTime(date.getTime() + (24*60*60*1000))  // One day expiry
        const expires = `expires=${date.toUTCString()}`
        document.cookie = `${name} = ${value}; ${expires}; path=/`
        return value
    }

    /**
     * Attempts to get the value of the A/B test cookie. If no cookie exists, returns null.
     */
    public static getABCookie(): any {
        const name = 'browsi_AB_test'
        const nameEQ = name + '='
        const allCookies = document.cookie.split(';')
        for (let cookie of allCookies) {
            while (cookie.charAt(0) === ' ') { cookie = cookie.substring(1, cookie.length) }
            if (cookie.indexOf(nameEQ) === 0) {
                const out = cookie.substring(nameEQ.length, cookie.length)
                return (out === 'true') ? true : false
            }
        }
        return undefined
    }

    /**
     * Parses page-data json and integrates Browsi. If browsi_integration is true and browsi_percentage
     * is 1, then the page will load with the Browsi configuration. If browsi_percentage is less than 1,
     * a cookie is made storing which A/B test variant will be used.
     * @param browsiPercentage A number between 0 and 1 describing proportion of A/B test buckets
     */
    public static integrateBrowsi(browsiPercentage: number): void {
        if (browsiPercentage === 1) {
            window.isBrowsi = true
            return
        }
        let ABCookie = this.getABCookie()
        if (ABCookie === undefined) {
            ABCookie = this.setABCookie(browsiPercentage)
        }
        window.isBrowsi = (!!ABCookie)
    }

    /**
     * @param ads - the initial array of ads to filter
     * @param banner - if true, return banner ads, if false, all other ads
     * returns the array of non-Browsi ads to be shown (e.g. native ads)
     */
    public static filterAds(ads: IAd[], banner: boolean): IAd[] {
        // filters all ads matching the following regex (e.g. ad-1, ad-23, etc.)
        const re = /^ad-\d+$/
        if (banner) {
            return ads.filter((ad) => ad.id.match(re))
        }
        return ads.filter((ad) => !ad.id.match(re))
    }

    /**
     * Returns an array containing the top ad on the page
     * @param ads All the ads on the page
     * @returns Only the top ad (with id ad-1)
     */
    public static getTopAd(ads: IAd[]): IAd[] {
        const re = /^ad-1$/
        return ads.filter((ad) => ad.id.match(re))
    }

    /**
     * Converts an array of ad sizes to a format compatible with Browsi's API
     * @param adSizes The ad sizes to be formatted
     * @returns A string representation of the ad sizes compatible with Browsi's API
     */
    public static formatAdSizes(adSizes: AdSize[]) {
        const adSizeFormatted = []
        // tslint:disable-next-line: no-magic-numbers
        for (const adSize of adSizes) {
            adSizeFormatted.push({w: adSize[0], h: adSize[1]})
        }
        return JSON.stringify(adSizeFormatted)
    }

    /**
     * @param conf the configuration holding page-data
     * @returns true or false whether the page is sponsored content
     */
    public static checkPageSponsored(conf: IConfiguration): boolean {
        return Array.isArray(conf.page.ancestry) ? conf.page.ancestry.includes('sponsored') : false
    }

    public static checkAdLight(): boolean {
        const profile = LocalStore.get<IProfile>(Store.ID)
        return profile ? profile.adLight : false
    }
}
